// A PARTNER-FELÜLET navigációja — EGY forrás a sidebarnak, a mobil alsó navnak és a mobil menünek.
//
// ÖT elem (a terv NÉGY lapja + a FEJLESZTÉSEK) — szemben a belső felület 21 menüpontjával
// (ebből 20 jog-szűrt + a kezdőlap, 6 csoportban). A partner jogképe FIÓKONKÉNTI (portál-szerep → feature-jog,
// `/portal/me` `accounts[].permissions`): a `permission` mezős elem CSAK akkor látszik, ha valamely fiókon
// megvan a jog (`visiblePortalNav`) — amit lát, azt használhatja is. Üzenetek és Profil jogfüggetlen.
//
// A LISTA CSAK MEGÉPÜLT lapot tartalmaz: egy „hamarosan" menüpont hamis ígéret, és a tipizált router is
// elhasalna egy nem létező route-on.
import type { PortalMeResponse } from '@atlas/contracts';
import { PORTAL_PERMISSIONS, type PortalPermission } from '@atlas/shared';
import {
  ChatBubbleLeftRightIcon,
  HomeIcon,
  MegaphoneIcon,
  TicketIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

import { portalPermissionsOf } from '@/features/portal/use-portal-permissions';

/** A jelvény-szám kiválasztása a keret-adatból; `null` → nincs jelvény (nem 0-t mutatunk). */
type BadgePick = (counts: PortalMeResponse['counts']) => number | null;

export interface PortalNavItem {
  /** A route útvonala (tipizált router — a `to` literál kell). */
  to:
    | '/portal'
    | '/portal/tickets'
    | '/portal/messages'
    | '/portal/invoices'
    | '/portal/product-updates';
  labelKey: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  badge?: BadgePick;
  /** Ha megadott, az elem csak akkor látszik, ha valamely fiókon megvan ez a jog. */
  permission?: PortalPermission;
}

// A menüpontok — mind MEGÉPÜLT lapra visz.
export const PORTAL_NAV: readonly PortalNavItem[] = [
  { to: '/portal', labelKey: 'portal.nav.home', Icon: HomeIcon },
  {
    to: '/portal/tickets',
    labelKey: 'portal.nav.tickets',
    Icon: TicketIcon,
    permission: PORTAL_PERMISSIONS.ticketsView,
    // A jelvény a FOLYAMATBAN lévő jegyek száma (a terv `counts.openTickets`-e).
    badge: (c) => (c.openTickets > 0 ? c.openTickets : null),
  },
  {
    to: '/portal/messages',
    labelKey: 'portal.nav.messages',
    Icon: ChatBubbleLeftRightIcon,
    // `null` számláló = a modul nem szolgálható ki (nincs gépi kredenciál) → NINCS jelvény, nem 0.
    badge: (c) => (c.unreadMessages !== null && c.unreadMessages > 0 ? c.unreadMessages : null),
  },
  {
    to: '/portal/invoices',
    labelKey: 'portal.nav.invoices',
    Icon: WalletIcon,
    permission: PORTAL_PERMISSIONS.billingView,
    // LEJÁRT számla — a terv szándékosan csak ezt jelvényezi (a „van számlám" nem hír).
    badge: (c) => (c.overdueInvoices !== null && c.overdueInvoices > 0 ? c.overdueInvoices : null),
  },
  {
    // FEJLESZTÉSEK — a belső felülethez hasonlóan a menü UTOLSÓ eleme: nem napi munka, hanem hír.
    // JELVÉNY SZÁNDÉKOSAN NINCS: a „van 3 új bejegyzés" számláló olvasottság-nyilvántartást igényelne
    // partnerenként, ami ehhez a tartalomhoz aránytalan (a keret-válasz sem hordoz ilyen számot).
    to: '/portal/product-updates',
    labelKey: 'portal.nav.productUpdates',
    Icon: MegaphoneIcon,
    permission: PORTAL_PERMISSIONS.productUpdatesView,
  },
] as const;

/**
 * A jogosult menüpontok. A keret-adat betöltéséig a jogos elemek REJTVE maradnak (nem villannak fel, majd
 * tűnnek el); a jogfüggetlen elemek (Kezdőlap, Üzenetek) azonnal ott vannak.
 */
export function visiblePortalNav(me: PortalMeResponse | undefined): readonly PortalNavItem[] {
  const perms = portalPermissionsOf(me);
  return PORTAL_NAV.filter((n) => n.permission === undefined || perms.has(n.permission));
}
